import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Refresh, Check, Close, Edit } from '@mui/icons-material';
import { recurringApi, billsApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { formatCurrency, formatDate } from '../utils/helpers';
import LoadingButton from '../components/LoadingButton';

const CADENCE = { weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Every 3 months', yearly: 'Yearly' };

const BILL_STATUS = {
  open: { label: 'Open', color: 'warning' },
  part_paid: { label: 'Part paid', color: 'warning' },
  paid: { label: 'Paid', color: 'success' },
  overdue: { label: 'Overdue', color: 'error' },
};

const billName = (bill) =>
  bill.biller || [bill.institution, bill.last4 && `xx${bill.last4}`].filter(Boolean).join(' ') || 'Bill';

/**
 * What's coming up: recurring payments (rent, EMIs, SIPs, subscriptions)
 * and bills read from email, plus suggestions to confirm.
 */
const Upcoming = () => {
  const { showSuccess, showError } = useNotification();
  const [days, setDays] = useState(30);
  const [upcoming, setUpcoming] = useState([]);
  const [series, setSeries] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [billDialog, setBillDialog] = useState({ open: false, bill: null });
  const [billForm, setBillForm] = useState({ totalDue: '', minimumDue: '', dueDate: '' });
  const [savingBill, setSavingBill] = useState(false);

  const load = useCallback(async () => {
    try {
      const [upcomingRes, seriesRes, billsRes] = await Promise.all([
        recurringApi.getUpcoming(days),
        recurringApi.getAll(),
        billsApi.getAll({ limit: 20 }),
      ]);
      setUpcoming(upcomingRes.data.items);
      setSeries(seriesRes.data.series);
      setBills(billsRes.data.bills);
    } catch (error) {
      showError('Failed to load upcoming payments');
    } finally {
      setLoading(false);
    }
  }, [days, showError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDetect = async () => {
    setDetecting(true);
    try {
      const response = await recurringApi.detect();
      const { created } = response.data;
      showSuccess(created ? `Found ${created} new recurring payment${created === 1 ? '' : 's'}` : 'No new recurring payments found');
      load();
    } catch (error) {
      showError('Failed to look for recurring payments');
    } finally {
      setDetecting(false);
    }
  };

  const setSeriesStatus = async (item, action) => {
    setBusyId(item.id);
    try {
      await recurringApi[action](item.id);
      showSuccess({
        confirm: `Tracking ${item.name}`,
        dismiss: `${item.name} dismissed`,
        end: `Stopped tracking ${item.name}`,
      }[action]);
      load();
    } catch (error) {
      showError('Failed to update the recurring payment');
    } finally {
      setBusyId(null);
    }
  };

  const openBill = (bill) => {
    setBillForm({
      totalDue: bill.totalDue ?? '',
      minimumDue: bill.minimumDue ?? '',
      dueDate: bill.dueDate || '',
    });
    setBillDialog({ open: true, bill });
  };

  const saveBill = async (markPaid = false) => {
    setSavingBill(true);
    try {
      // Only figures that were filled in and changed
      const bill = billDialog.bill;
      const data = markPaid ? { markPaid: true } : {};
      if (billForm.totalDue !== '' && Number(billForm.totalDue) !== bill.totalDue) data.totalDue = billForm.totalDue;
      if (billForm.minimumDue !== '' && Number(billForm.minimumDue) !== bill.minimumDue) data.minimumDue = billForm.minimumDue;
      if (billForm.dueDate && billForm.dueDate !== bill.dueDate) data.dueDate = billForm.dueDate;
      await billsApi.update(bill.id, data);
      showSuccess(markPaid ? 'Bill marked as paid' : 'Bill saved');
      setBillDialog({ open: false, bill: null });
      load();
    } catch (error) {
      showError('Failed to save the bill');
    } finally {
      setSavingBill(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  const suggested = series.filter(s => s.status === 'suggested');
  const active = series.filter(s => s.status === 'active');

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography variant="h4">Upcoming</Typography>
        <LoadingButton variant="outlined" startIcon={<Refresh />} onClick={handleDetect} loading={detecting}>
          Look for recurring payments
        </LoadingButton>
      </Stack>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <CardHeader
              title="Due soon"
              action={
                <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
                  <ToggleButton value={7}>7 days</ToggleButton>
                  <ToggleButton value={30}>30 days</ToggleButton>
                  <ToggleButton value={90}>90 days</ToggleButton>
                </ToggleButtonGroup>
              }
            />
            <CardContent>
              {upcoming.length === 0 ? (
                <Alert severity="info">
                  Nothing due in the next {days} days. Confirm recurring payments below, or forward card
                  statements to see bills here.
                </Alert>
              ) : (
                <List disablePadding>
                  {upcoming.map((item, index) => (
                    <React.Fragment key={`${item.kind}-${item.id}`}>
                      {index > 0 && <Divider component="li" />}
                      <ListItem sx={{ pr: 16 }}>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="body1">{item.name}</Typography>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={item.kind === 'bill' ? 'Bill' : CADENCE[item.cadence] || 'Recurring'}
                              />
                              {item.overdue && <Chip size="small" color="error" label="Overdue" />}
                              {item.needsPdf && <Chip size="small" color="warning" variant="outlined" label="Figures in PDF" />}
                            </Stack>
                          }
                          secondary={[
                            `Due ${formatDate(item.dueDate)}`,
                            // Tells apart several payments to one payee (four SIPs to Indian Clearing Corp)
                            item.category,
                            item.account,
                            item.minimumDue ? `minimum ${formatCurrency(item.minimumDue)}` : null,
                          ].filter(Boolean).join(' · ')}
                        />
                        <ListItemSecondaryAction>
                          <Typography
                            variant="body1"
                            color={item.direction === 'credit' ? 'success.main' : 'text.primary'}
                          >
                            {item.amount === null ? '—' : `${item.direction === 'credit' ? '+' : ''}${formatCurrency(item.amount)}`}
                          </Typography>
                        </ListItemSecondaryAction>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ mb: 2 }}>
            <CardHeader
              title="Is this a recurring payment?"
              subheader="Found in your transactions; confirm to track it"
            />
            <CardContent>
              {suggested.length === 0 ? (
                <Alert severity="info">No suggestions right now.</Alert>
              ) : (
                <List disablePadding>
                  {suggested.map((item, index) => (
                    <React.Fragment key={item.id}>
                      {index > 0 && <Divider component="li" />}
                      <ListItem disableGutters sx={{ gap: 2 }}>
                        <ListItemText
                          sx={{ minWidth: 0 }}
                          primary={`${item.name} · ${item.direction === 'credit' ? '+' : ''}${formatCurrency(item.expectedAmount)}`}
                          secondary={[
                            CADENCE[item.cadence],
                            item.category?.name,
                            `seen ${item.occurrences} times`,
                            // A "next" date already past says nothing: when it was last seen does
                            item.missedSince ? `last ${formatDate(item.lastSeenDate)}` : `next ${formatDate(item.nextDueDate)}`,
                          ].filter(Boolean).join(' · ')}
                        />
                        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<Check />}
                            disabled={busyId === item.id}
                            onClick={() => setSeriesStatus(item, 'confirm')}
                          >
                            Track
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Close />}
                            disabled={busyId === item.id}
                            onClick={() => setSeriesStatus(item, 'dismiss')}
                          >
                            No
                          </Button>
                        </Stack>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader title="Tracked recurring payments" />
            <CardContent>
              {active.length === 0 ? (
                <Alert severity="info">None yet. Confirm a suggestion to track it.</Alert>
              ) : (
                <List disablePadding>
                  {active.map((item, index) => (
                    <React.Fragment key={item.id}>
                      {index > 0 && <Divider component="li" />}
                      <ListItem disableGutters sx={{ gap: 2 }}>
                        <ListItemText
                          sx={{ minWidth: 0 }}
                          primary={`${item.name} · ${item.direction === 'credit' ? '+' : ''}${formatCurrency(item.expectedAmount)}`}
                          secondary={
                            <>
                              {[
                                CADENCE[item.cadence],
                                item.category?.name,
                                !item.missedSince && item.nextDueDate && `next ${formatDate(item.nextDueDate)}`,
                                item.lastAmount && item.lastAmount !== item.expectedAmount && `last ${formatCurrency(item.lastAmount)}`,
                              ].filter(Boolean).join(' · ')}
                              {item.missedSince && (
                                <Typography component="span" variant="body2" color="warning.main" display="block">
                                  Nothing since {formatDate(item.lastSeenDate)} (was due {formatDate(item.missedSince)}). Stopped?
                                </Typography>
                              )}
                            </>
                          }
                        />
                        <Button
                          size="small"
                          sx={{ flexShrink: 0 }}
                          disabled={busyId === item.id}
                          onClick={() => setSeriesStatus(item, 'end')}
                        >
                          Stop tracking
                        </Button>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card>
            <CardHeader title="Bills" subheader="Card statements and bill reminders from your emails" />
            <CardContent>
              {bills.length === 0 ? (
                <Alert severity="info">
                  No bills yet. Card statements and bill reminders become bills as they arrive. For
                  statements already received, save their PDF passwords in Settings, Email, Statement passwords.
                </Alert>
              ) : (
                <List disablePadding>
                  {bills.map((bill, index) => (
                    <React.Fragment key={bill.id}>
                      {index > 0 && <Divider component="li" />}
                      <ListItem sx={{ pr: 8 }}>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="body1">{billName(bill)}</Typography>
                              <Chip size="small" label={BILL_STATUS[bill.status]?.label || bill.status} color={BILL_STATUS[bill.status]?.color || 'default'} />
                              {bill.needsPdf && <Chip size="small" variant="outlined" color="warning" label="Figures in PDF" />}
                            </Stack>
                          }
                          secondary={[
                            bill.totalDue !== null && `Due ${formatCurrency(bill.totalDue)}`,
                            bill.dueDate && `by ${formatDate(bill.dueDate)}`,
                            bill.amountPaid > 0 && `paid ${formatCurrency(bill.amountPaid)}`,
                            bill.statementDate && `statement ${formatDate(bill.statementDate)}`,
                          ].filter(Boolean).join(' · ')}
                        />
                        <ListItemSecondaryAction>
                          <Button size="small" startIcon={<Edit />} onClick={() => openBill(bill)}>
                            Edit
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={billDialog.open} onClose={() => setBillDialog({ open: false, bill: null })} maxWidth="xs" fullWidth>
        <DialogTitle>{billDialog.bill && billName(billDialog.bill)}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {billDialog.bill?.needsPdf && (
              <Alert severity="info">
                This statement's figures are only in its password-protected PDF. Open it and fill them in here.
              </Alert>
            )}
            <TextField
              label="Total due"
              type="number"
              value={billForm.totalDue}
              onChange={(e) => setBillForm({ ...billForm, totalDue: e.target.value })}
              fullWidth
            />
            <TextField
              label="Minimum due"
              type="number"
              value={billForm.minimumDue}
              onChange={(e) => setBillForm({ ...billForm, minimumDue: e.target.value })}
              fullWidth
            />
            <TextField
              label="Due date"
              type="date"
              value={billForm.dueDate}
              onChange={(e) => setBillForm({ ...billForm, dueDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBillDialog({ open: false, bill: null })}>Cancel</Button>
          {billDialog.bill?.status !== 'paid' && (
            <Button onClick={() => saveBill(true)} disabled={savingBill}>
              Mark as paid
            </Button>
          )}
          <LoadingButton variant="contained" onClick={() => saveBill(false)} loading={savingBill}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Upcoming;
